const FROZEN_PANE_COLUMN_ATTRIBUTE = 'data-xlsx-frozen-pane-column';
const FROZEN_PANE_COLUMN_BOUNDARY_ATTRIBUTE = 'data-xlsx-frozen-pane-column-boundary';
const FROZEN_PANE_ROW_ATTRIBUTE = 'data-xlsx-frozen-pane-row';
const FROZEN_PANE_ROW_BOUNDARY_ATTRIBUTE = 'data-xlsx-frozen-pane-row-boundary';
const CUSTOM_FREEZE_COLUMN_ATTRIBUTE = 'data-xlsx-custom-freeze-column';
const CUSTOM_FREEZE_ROW_ATTRIBUTE = 'data-xlsx-custom-freeze-row';
const PINNED_ROW_PASSTHROUGH_ATTRIBUTE = 'data-xlsx-pinned-row-passthrough';
const PINNED_COLUMN_PASSTHROUGH_ATTRIBUTE = 'data-xlsx-pinned-column-passthrough';
const ORIGINAL_BACKGROUND_COLOR_ATTRIBUTE = 'data-xlsx-original-background-color';
const ORIGINAL_BACKGROUND_PRIORITY_ATTRIBUTE = 'data-xlsx-original-background-priority';
const FROZEN_PANE_ATTRIBUTES = [
  FROZEN_PANE_COLUMN_ATTRIBUTE,
  FROZEN_PANE_COLUMN_BOUNDARY_ATTRIBUTE,
  FROZEN_PANE_ROW_ATTRIBUTE,
  FROZEN_PANE_ROW_BOUNDARY_ATTRIBUTE,
] as const;
const FROZEN_PANE_STYLE_PROPERTIES = [
  '--xlsx-frozen-pane-background-color',
  '--xlsx-frozen-pane-column-offset',
  '--xlsx-frozen-pane-row-top',
] as const;

interface ApplySpreadsheetFreezePaneOptions {
  activeContent: HTMLDivElement;
  columnCellKey?: string;
  rowCellKey?: string;
}

interface GetFrozenPaneTranslateXOptions {
  currentTranslateX: number;
  visualDelta: number;
  visualScale: number;
}

export const getFrozenPaneTranslateX = ({
  currentTranslateX,
  visualDelta,
  visualScale,
}: GetFrozenPaneTranslateXOptions): number =>
  visualScale > 0 ? Math.max(0, currentTranslateX + visualDelta / visualScale) : currentTranslateX;

const getCellByKey = (root: HTMLElement | null, cellKey: string | undefined) =>
  cellKey ? (root?.querySelector<HTMLElement>(`#${CSS.escape(`CELL-${cellKey}`)}`) ?? null) : null;

const getLogicalRowIndex = (row: HTMLTableRowElement): string | undefined =>
  row.querySelector<HTMLElement>('td[data-row-index]')?.dataset.rowIndex;

const isOpaqueBackgroundColor = (backgroundColor: string): boolean => {
  if (!backgroundColor || backgroundColor === 'transparent') {
    return false;
  }
  const rgbaMatch = backgroundColor.match(/^rgba\([^,]+,[^,]+,[^,]+,\s*([\d.]+)\)$/);
  return !rgbaMatch || Number(rgbaMatch[1]) >= 1;
};

const getOpaqueBackgroundColor = (cell: HTMLElement): string => {
  const ownerWindow = cell.ownerDocument.defaultView ?? window;
  let element: HTMLElement | null = cell;

  while (element) {
    const { backgroundColor } = ownerWindow.getComputedStyle(element);
    if (isOpaqueBackgroundColor(backgroundColor)) {
      return backgroundColor;
    }
    element = element.parentElement;
  }

  return '#FFFFFF';
};

const clearSpreadsheetFreezePane = (activeContent: HTMLDivElement) => {
  activeContent.removeAttribute('data-xlsx-custom-freeze-pane');
  activeContent.removeAttribute(CUSTOM_FREEZE_COLUMN_ATTRIBUTE);
  activeContent.removeAttribute(CUSTOM_FREEZE_ROW_ATTRIBUTE);
  activeContent
    .querySelectorAll<HTMLElement>(FROZEN_PANE_ATTRIBUTES.map(attribute => `[${attribute}]`).join(','))
    .forEach(cell => {
      FROZEN_PANE_ATTRIBUTES.forEach(attribute => cell.removeAttribute(attribute));
      FROZEN_PANE_STYLE_PROPERTIES.forEach(property => cell.style.removeProperty(property));
      const originalBackgroundColor = cell.getAttribute(ORIGINAL_BACKGROUND_COLOR_ATTRIBUTE) ?? '';
      const originalBackgroundPriority = cell.getAttribute(ORIGINAL_BACKGROUND_PRIORITY_ATTRIBUTE) ?? '';
      if (originalBackgroundColor) {
        cell.style.setProperty('background-color', originalBackgroundColor, originalBackgroundPriority);
      } else {
        cell.style.removeProperty('background-color');
      }
      cell.removeAttribute(ORIGINAL_BACKGROUND_COLOR_ATTRIBUTE);
      cell.removeAttribute(ORIGINAL_BACKGROUND_PRIORITY_ATTRIBUTE);
    });
  activeContent
    .querySelectorAll<HTMLElement>(`[${PINNED_ROW_PASSTHROUGH_ATTRIBUTE}]`)
    .forEach(row => row.removeAttribute(PINNED_ROW_PASSTHROUGH_ATTRIBUTE));
  activeContent
    .querySelectorAll<HTMLElement>(`[${PINNED_COLUMN_PASSTHROUGH_ATTRIBUTE}]`)
    .forEach(cell => cell.removeAttribute(PINNED_COLUMN_PASSTHROUGH_ATTRIBUTE));
};

const markFrozenPaneCell = (cell: HTMLElement, attribute: string) => {
  if (!cell.hasAttribute(ORIGINAL_BACKGROUND_COLOR_ATTRIBUTE)) {
    cell.setAttribute(ORIGINAL_BACKGROUND_COLOR_ATTRIBUTE, cell.style.getPropertyValue('background-color'));
    cell.setAttribute(ORIGINAL_BACKGROUND_PRIORITY_ATTRIBUTE, cell.style.getPropertyPriority('background-color'));
  }
  const opaqueBackgroundColor = getOpaqueBackgroundColor(cell);
  cell.setAttribute(attribute, 'true');
  cell.style.setProperty('--xlsx-frozen-pane-background-color', opaqueBackgroundColor);
  cell.style.setProperty('background-color', opaqueBackgroundColor, 'important');
};

export const applySpreadsheetFreezePane = ({
  activeContent,
  columnCellKey,
  rowCellKey,
}: ApplySpreadsheetFreezePaneOptions) => {
  clearSpreadsheetFreezePane(activeContent);
  let paneObserver: MutationObserver | undefined;
  let markMatchingRowCells: (() => void) | undefined;
  let markPrecedingTitleRows: (() => void) | undefined;
  let markMatchingColumnCells: (() => void) | undefined;

  const cleanup = () => {
    paneObserver?.disconnect();
    clearSpreadsheetFreezePane(activeContent);
  };

  const contentColumns = activeContent.querySelector<HTMLElement>('[id="spreadsheet-table__content-columns"]');
  if (!contentColumns || (!columnCellKey && !rowCellKey)) {
    return cleanup;
  }

  activeContent.setAttribute('data-xlsx-custom-freeze-pane', 'true');
  const titleColumnsContainer = activeContent.querySelector<HTMLElement>('.titles-columns-container');

  if (rowCellKey) {
    const selectedCell = getCellByKey(contentColumns, rowCellKey) ?? getCellByKey(titleColumnsContainer, rowCellKey);
    const selectedRow = selectedCell?.closest('tr') as HTMLTableRowElement | null;
    const selectedLogicalRowIndex = selectedRow ? getLogicalRowIndex(selectedRow) : undefined;
    if (!selectedLogicalRowIndex) {
      return cleanup;
    }

    const contentRows = Array.from(contentColumns.querySelectorAll<HTMLTableRowElement>('tr')).filter(
      (row): row is HTMLTableRowElement => row.tagName === 'TR' && Boolean(row.querySelector('td[data-row-index]'))
    );
    if (!contentRows.some(row => getLogicalRowIndex(row) === selectedLogicalRowIndex)) {
      return cleanup;
    }

    activeContent.setAttribute(CUSTOM_FREEZE_ROW_ATTRIBUTE, 'true');
    const columnLegendHeight = contentColumns.querySelector<HTMLElement>('td.col-legend')?.offsetHeight ?? 0;
    const markPrecedingRows = (container: HTMLElement | null) => {
      container?.querySelectorAll<HTMLTableRowElement>('tr').forEach(row => {
        const logicalRowIndex = getLogicalRowIndex(row);
        if (logicalRowIndex !== undefined && Number(logicalRowIndex) < Number(selectedLogicalRowIndex)) {
          row.setAttribute(PINNED_ROW_PASSTHROUGH_ATTRIBUTE, 'true');
        } else {
          row.removeAttribute(PINNED_ROW_PASSTHROUGH_ATTRIBUTE);
        }
      });
    };
    markPrecedingTitleRows = () => markPrecedingRows(titleColumnsContainer);
    markPrecedingTitleRows();
    markMatchingRowCells = () => {
      // Virtualized rows can mount after the freeze boundary is selected.
      markPrecedingRows(contentColumns);
      const mountedContentRow = contentColumns
        .querySelector<HTMLElement>(`td[data-row-index="${CSS.escape(selectedLogicalRowIndex)}"]`)
        ?.closest('tr') as HTMLTableRowElement | null;
      const mountedTitleRow = titleColumnsContainer
        ?.querySelector<HTMLElement>(`td[data-row-index="${CSS.escape(selectedLogicalRowIndex)}"]`)
        ?.closest('tr') as HTMLTableRowElement | null;
      const frozenCells = [
        ...(mountedContentRow ? (Array.from(mountedContentRow.cells) as HTMLElement[]) : []),
        ...(mountedTitleRow ? (Array.from(mountedTitleRow.cells) as HTMLElement[]) : []),
      ];

      frozenCells.forEach(cell => {
        markFrozenPaneCell(cell, FROZEN_PANE_ROW_ATTRIBUTE);
        cell.style.setProperty('--xlsx-frozen-pane-row-top', `${columnLegendHeight}px`);
        cell.setAttribute(FROZEN_PANE_ROW_BOUNDARY_ATTRIBUTE, 'true');
      });
    };
    markMatchingRowCells();
  }

  if (columnCellKey) {
    const selectedCell = getCellByKey(contentColumns, columnCellKey);
    const selectedColumnLegend = selectedCell?.dataset.columnLegend ?? columnCellKey.match(/^[^\d]+/)?.[0];
    if (!selectedColumnLegend) {
      return cleanup;
    }

    const escapedColumnLegend = CSS.escape(selectedColumnLegend);
    const matchingColumnSelector = `td[data-column-legend="${escapedColumnLegend}"]`;
    const selectedColumnCell =
      contentColumns.querySelector<HTMLElement>(`td.col-legend[data-column-legend="${escapedColumnLegend}"]`) ??
      contentColumns.querySelector<HTMLElement>(matchingColumnSelector);
    if (!selectedColumnCell) {
      return cleanup;
    }

    activeContent.setAttribute(CUSTOM_FREEZE_COLUMN_ATTRIBUTE, 'true');
    const selectedColumnIndex = Array.from(selectedColumnCell.parentElement?.children ?? []).indexOf(
      selectedColumnCell
    );
    const precedingColumnSelectors = Array.from(selectedColumnCell.parentElement?.children ?? [])
      .slice(0, selectedColumnIndex)
      .map(cell => (cell as HTMLElement).dataset.columnLegend)
      .filter((legend): legend is string => Boolean(legend))
      .map(legend => `td[data-column-legend="${CSS.escape(legend)}"]`);
    markMatchingColumnCells = () => {
      if (precedingColumnSelectors.length) {
        contentColumns.querySelectorAll<HTMLElement>(precedingColumnSelectors.join(',')).forEach(cell => {
          cell.setAttribute(PINNED_COLUMN_PASSTHROUGH_ATTRIBUTE, 'true');
        });
      }
      contentColumns.querySelectorAll<HTMLElement>(matchingColumnSelector).forEach(cell => {
        markFrozenPaneCell(cell, FROZEN_PANE_COLUMN_ATTRIBUTE);
        cell.style.setProperty('--xlsx-frozen-pane-column-offset', '0px');
        cell.setAttribute(FROZEN_PANE_COLUMN_BOUNDARY_ATTRIBUTE, 'true');
      });
    };

    markMatchingColumnCells();
  }

  const ownerWindow = contentColumns.ownerDocument.defaultView ?? window;
  paneObserver = new ownerWindow.MutationObserver(mutations => {
    if (titleColumnsContainer && mutations.some(({ target }) => titleColumnsContainer.contains(target))) {
      markPrecedingTitleRows?.();
    }
    markMatchingRowCells?.();
    markMatchingColumnCells?.();
  });
  paneObserver.observe(contentColumns, { childList: true, subtree: true });
  if (titleColumnsContainer) {
    paneObserver.observe(titleColumnsContainer, { childList: true, subtree: true });
  }

  return cleanup;
};
